import { Stack, Card, Typography, useMediaQuery, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { usePoliciesQuery } from '../../../api/getPolicies';
import colors from '../../../constants/colors';
import useAnalytics from '../../../hooks/useAnalytics';
import theme from '../../../styles/theme';
import Button from '../../styledComponents/Button';

const StartClaimCard = () => {
  const { track } = useAnalytics();

  const isTablet = useMediaQuery(theme.breakpoints.only('tablet'));
  const { data: policiesData } = usePoliciesQuery();

  const hasAutoPolicy = policiesData?.policies?.find(
    (policy) => policy.policyTypeCd === 'PA'
  );

  const hasPropertyPolicy = policiesData?.policies?.find(
    (policy) => policy.policyTypeCd === 'HO'
  );

  const handleStartGlassClaimClick = () => {
    track('Claim Initiated', {
      event_type: 'Link Accessed',
      location: 'Secondary Card',
      claim_type: 'glass',
    });
  };

  const handleStartAutoClaimClick = () => {
    track('Claim Initiated', {
      event_type: 'Link Accessed',
      location: 'Secondary Card',
      claim_type: 'Auto',
      policy_type: 'Auto',
      claim_product_type: 'PA',
    });
  };

  const handleStartPropertyClaimClick = () => {
    track('Claim Initiated', {
      event_type: 'Link Accessed',
      location: 'Secondary Card',
      link: 'Home',
      claim_type: 'Property',
      policy_type: 'Property',
      claim_product_type: 'HO',
    });
  };

  return (
    <Card
      data-testid="startClaimCard"
      component={Stack}
      spacing={{ mobile: 2, tablet: 2, desktop: 3 }}
      sx={{
        boxShadow: '0px 4px 8px rgba(25, 30, 43, 0.08)',
        px: { mobile: 2, tablet: 5, desktop: 2 },
        py: { mobile: 2, tablet: 3, desktop: 2 },
        borderRadius: 2,
        height: 'auto',
      }}
    >
      <Stack
        direction={{ mobile: 'column', tablet: 'row', desktop: 'column' }}
        alignItems={{ mobile: 'start', tablet: 'flex-start', desktop: 'start' }}
        sx={{ justifyContent: { tablet: 'space-between' } }}
      >
        <Stack sx={{ mb: { mobile: 2, tablet: 0, desktop: 2 } }} spacing={1}>
          <Typography
            variant="h2"
            sx={{
              fontSize: 18,
              fontWeight: 700,
              color: colors.csaaBlack,
              lineHeight: '24px',
              width: 1,
              letterSpacing: '0.4px',
            }}
          >
            Start a claim
          </Typography>
          {isTablet &&
            (import.meta.env.VITE_PROPERTY_CLAIM_ENABLED === 'true' &&
            hasPropertyPolicy ? (
              <Typography
                sx={{
                  fontFamily: 'Cabin',
                  fontKerning: 'normal',
                  fontWeight: 400,
                  fontSize: { mobile: 14, tablet: 16 },
                  color: colors.csaaDark,
                  letterSpacing: 0.4,
                  lineHeight: '14px',
                }}
              >
                Home includes homeowners, condo and renters claims.
              </Typography>
            ) : (
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Cabin',
                    fontKerning: 'normal',
                    fontWeight: 400,
                    fontSize: { mobile: 14, tablet: 16 },
                    color: colors.csaaDark,
                    letterSpacing: 0.4,
                    lineHeight: '14px',
                  }}
                >
                  Property claim,
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Cabin',
                    fontSize: { mobile: 14, tablet: 16 },
                    color: colors.csaaDark,
                    letterSpacing: 0.4,
                    lineHeight: '14px',
                    ml: 0.5,
                  }}
                >
                  call
                </Typography>
                <Link
                  aria-label="Call CSAA customer support"
                  data-testid="csaaClaimsSupportPhoneNumber"
                  href="tel:+800.922.8228"
                  underline="none"
                  sx={{
                    fontWeight: 700,
                    fontSize: { mobile: 14, tablet: 16 },
                    fontFamily: 'Cabin',
                    fontKerning: 'normal',
                    color: colors.csaaSpectrumBlue50,
                    lineHeight: '14px',
                    ml: 0.5,
                  }}
                  onClick={() =>
                    track('Contact Accessed', {
                      dialed_number: '800.922.8228',
                      event_type: 'Link Accessed',
                      contents: 'Auto or property claim',
                      link: '800.922.8228',
                      location: 'Secondary Card',
                      method: 'Call',
                    })
                  }
                >
                  800.922.8228
                </Link>
                <Typography
                  sx={{
                    fontFamily: 'Cabin',
                    fontSize: { mobile: 14, tablet: 16 },
                    color: colors.csaaDark,
                    letterSpacing: 0.4,
                    lineHeight: '14px',
                  }}
                >
                  , 24/7
                </Typography>
              </Stack>
            ))}
        </Stack>
        <Stack
          direction={{ mobile: 'column', tablet: 'column', desktop: 'column' }}
          spacing={{ mobile: 2, tablet: 2, desktop: 2 }}
          sx={{
            width: { mobile: 1, tablet: 0.3, desktop: 1 },
            alignItems: {
              mobile: 'stretch',
              tablet: 'stretch',
              desktop: 'stretch',
            },
          }}
        >
          {hasAutoPolicy && (
            <Button
              component={RouterLink}
              role="link"
              to="/claims/auto"
              variant="outlined"
              color="inherit"
              data-testid="autoClaimCardStartClaimButton"
              onClick={handleStartAutoClaimClick}
              sx={{
                width: 1,
                borderWidth: 2,
                fontWeight: 700,
                textTransform: 'none',
                fontSize: 18,
                color: colors.csaaBlack,
                px: { mobile: 4, desktop: 3 },
                wordWrap: 'nowrap',
                minWidth: 'max-content',
              }}
            >
              Auto
            </Button>
          )}
          {hasAutoPolicy && (
            <Button
              component={RouterLink}
              role="link"
              to="/claims/glass"
              variant="outlined"
              color="inherit"
              data-testid="glassClaimCardStartClaimButton"
              onClick={handleStartGlassClaimClick}
              sx={{
                width: 1,
                borderWidth: 2,
                fontWeight: 700,
                textTransform: 'none',
                fontSize: 18,
                color: colors.csaaBlack,
                px: { mobile: 4, desktop: 3 },
                wordWrap: 'nowrap',
                minWidth: 'max-content',
              }}
            >
              Auto glass
            </Button>
          )}
          {hasPropertyPolicy &&
            import.meta.env.VITE_PROPERTY_CLAIM_ENABLED === 'true' && (
              <Button
                component={RouterLink}
                role="link"
                to="/claims/property"
                variant="outlined"
                color="inherit"
                data-testid="propertyClaimCardStartClaimButton"
                onClick={handleStartPropertyClaimClick}
                sx={{
                  width: 1,
                  borderWidth: 2,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: 18,
                  color: colors.csaaBlack,
                  px: { mobile: 4, desktop: 3 },
                  wordWrap: 'nowrap',
                  minWidth: 'max-content',
                }}
              >
                Home
              </Button>
            )}
        </Stack>
      </Stack>
      <Stack
        direction={{ mobile: 'column', tablet: 'row', desktop: 'column' }}
        sx={{
          alignItems: { mobile: 'start', tablet: 'center', desktop: 'start' },
          justifyContent: { tablet: 'space-between' },
          lineHeight: '14px',
        }}
      >
        {!isTablet &&
          (import.meta.env.VITE_PROPERTY_CLAIM_ENABLED === 'true' &&
          hasPropertyPolicy ? (
            <Stack>
              <Typography
                sx={{
                  fontFamily: 'Cabin',
                  fontKerning: 'normal',
                  fontWeight: 400,
                  fontSize: { mobile: 14, tablet: 16 },
                  color: colors.csaaDark,
                  letterSpacing: 0.4,
                  lineHeight: '14px',
                }}
              >
                Home includes homeowners, condo and renters claims.
              </Typography>
            </Stack>
          ) : (
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Cabin',
                  fontKerning: 'normal',
                  fontWeight: 400,
                  fontSize: { mobile: 14, tablet: 16 },
                  color: colors.csaaDark,
                  letterSpacing: 0.4,
                  lineHeight: '14px',
                }}
              >
                Property claim,
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Cabin',
                  fontSize: { mobile: 14, tablet: 16 },
                  color: colors.csaaDark,
                  letterSpacing: 0.4,
                  lineHeight: '14px',
                  ml: 0.5,
                }}
              >
                call
              </Typography>
              <Link
                aria-label="Call CSAA customer support"
                data-testid="csaaClaimsSupportPhoneNumber"
                href="tel:+800.922.8228"
                underline="none"
                sx={{
                  fontWeight: 700,
                  fontSize: { mobile: 14, tablet: 16 },
                  fontFamily: 'Cabin',
                  fontKerning: 'normal',
                  color: colors.csaaSpectrumBlue50,
                  lineHeight: '14px',
                  ml: 0.5,
                }}
                onClick={() =>
                  track('Contact Accessed', {
                    dialed_number: '800.922.8228',
                    event_type: 'Link Accessed',
                    contents: 'Auto or property claim',
                    link: '800.922.8228',
                    location: 'Secondary Card',
                    method: 'Call',
                  })
                }
              >
                800.922.8228
              </Link>
              <Typography
                sx={{
                  fontFamily: 'Cabin',
                  fontSize: { mobile: 14, tablet: 16 },
                  color: colors.csaaDark,
                  letterSpacing: 0.4,
                  lineHeight: '14px',
                }}
              >
                , 24/7
              </Typography>
            </Stack>
          ))}
      </Stack>
    </Card>
  );
};

export default StartClaimCard;
